// Roles and the permission matrix.
//
// The matrix's shape comes from the database, not from a constant in this
// file: permission_modules gives the rows, permission_catalog gives which cells
// exist at all. A pair absent from the catalog renders as "—" and cannot be
// granted — role_permissions has a foreign key onto it. Adding a module is
// therefore a migration, and the UI picks it up with no change here.

import { supabase } from '@/lib/supabase';

/**
 * The one role the matrix cannot narrow. Migration 0043 has the database grant
 * it the whole catalog, approval level 3, no rayon scope and owner-level legacy
 * access whatever is saved here, so the editor shows it locked rather than
 * offering edits that would be ignored or refused.
 */
export const SUPERADMIN_ROLE_ID = 'superadmin';

export interface PermissionModule {
  id: string;
  label: string;
  sort: number;
}

export interface PermissionCell {
  module: string;
  action: string;
  label: string;
  /** Renders under "Lainnya" instead of a shared action column. */
  isExtra: boolean;
  sort: number;
}

export interface AppRole {
  id: string;
  name: string;
  isBuiltin: boolean;
  /** null = not an approver. */
  approvalLevel: number | null;
  scopeLimited: boolean;
  legacyRole: 'owner' | 'cashier';
  /** Granted permissions as "module.action". */
  permissions: string[];
  /** Number of active users holding this role. */
  userCount: number;
}

/** The six actions that get their own column, in display order. */
export const SHARED_ACTIONS = [
  { action: 'view', label: 'Lihat' },
  { action: 'create', label: 'Tambah' },
  { action: 'edit', label: 'Edit' },
  { action: 'delete', label: 'Hapus' },
  { action: 'export', label: 'Export' },
  { action: 'import', label: 'Import' },
] as const;

export interface PermissionMatrixShape {
  modules: PermissionModule[];
  cells: PermissionCell[];
}

export async function fetchMatrixShape(): Promise<PermissionMatrixShape> {
  const [mods, cat] = await Promise.all([
    supabase.from('permission_modules').select('*').order('sort'),
    supabase.from('permission_catalog').select('*').order('sort'),
  ]);
  if (mods.error) throw new Error(mods.error.message);
  if (cat.error) throw new Error(cat.error.message);

  return {
    modules: (mods.data ?? []).map((m) => ({
      id: m.id as string,
      label: m.label as string,
      sort: m.sort as number,
    })),
    cells: (cat.data ?? []).map((c) => ({
      module: c.module as string,
      action: c.action as string,
      label: c.label as string,
      isExtra: c.is_extra as boolean,
      sort: c.sort as number,
    })),
  };
}

export async function listRoles(): Promise<AppRole[]> {
  const [roles, perms, holders] = await Promise.all([
    supabase.from('roles').select('*').order('is_builtin', { ascending: false }).order('name'),
    supabase.from('role_permissions').select('*'),
    // Only active holders: a role held solely by disabled accounts is free to
    // edit, and saying "3 user" about three people who cannot log in is a lie
    // that makes someone hesitate over a safe change.
    supabase.from('profiles').select('role_id').eq('is_active', true),
  ]);
  if (roles.error) throw new Error(roles.error.message);
  if (perms.error) throw new Error(perms.error.message);
  if (holders.error) throw new Error(holders.error.message);

  const byRole = new Map<string, string[]>();
  for (const p of perms.data ?? []) {
    const key = p.role_id as string;
    const list = byRole.get(key) ?? [];
    list.push(`${p.module as string}.${p.action as string}`);
    byRole.set(key, list);
  }

  const counts = new Map<string, number>();
  for (const h of holders.data ?? []) {
    const key = h.role_id as string;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return (roles.data ?? []).map((r) => ({
    id: r.id as string,
    name: r.name as string,
    isBuiltin: r.is_builtin as boolean,
    approvalLevel: (r.approval_level as number | null) ?? null,
    scopeLimited: r.scope_limited as boolean,
    legacyRole: r.legacy_role as 'owner' | 'cashier',
    permissions: (byRole.get(r.id as string) ?? []).sort(),
    userCount: counts.get(r.id as string) ?? 0,
  }));
}

export interface RoleAttributes {
  name: string;
  approvalLevel: number | null;
  scopeLimited: boolean;
  legacyRole: 'owner' | 'cashier';
}

export async function createRole(
  id: string,
  attrs: RoleAttributes,
  permissions: string[],
): Promise<void> {
  const { error } = await supabase.from('roles').insert({
    id,
    name: attrs.name,
    approval_level: attrs.approvalLevel,
    scope_limited: attrs.scopeLimited,
    legacy_role: attrs.legacyRole,
    is_builtin: false,
  });
  if (error) throw new Error(error.message);
  await replacePermissions(id, permissions);
}

/**
 * Save a role's attributes and its whole matrix.
 *
 * The matrix is written as delete-all-then-insert-ticked rather than a diff.
 * A diff would need to know what the row looked like when the screen loaded,
 * and two people editing the same role would each apply their own half. This
 * way the last save wins in full, which is at least a state someone chose.
 *
 * The two statements are not in one transaction — PostgREST has no way to ask
 * for that from the browser. The window where a role holds no permissions is
 * between two round-trips, and the deferred `role_permissions_keep_a_manager`
 * constraint does not span them, so a failure mid-save can leave a role
 * emptied. Recovery is to save again; the alternative is an RPC, which is
 * where this should go if it ever bites.
 */
export async function updateRole(
  id: string,
  attrs: RoleAttributes,
  permissions: string[],
): Promise<void> {
  const { error } = await supabase
    .from('roles')
    .update({
      name: attrs.name,
      approval_level: attrs.approvalLevel,
      scope_limited: attrs.scopeLimited,
      legacy_role: attrs.legacyRole,
    })
    .eq('id', id);
  if (error) throw new Error(error.message);
  await replacePermissions(id, permissions);
}

async function replacePermissions(roleId: string, permissions: string[]): Promise<void> {
  const del = await supabase.from('role_permissions').delete().eq('role_id', roleId);
  if (del.error) throw new Error(del.error.message);

  const rows = permissions.map((p) => {
    const dot = p.indexOf('.');
    return { role_id: roleId, module: p.slice(0, dot), action: p.slice(dot + 1) };
  });
  if (rows.length === 0) return;

  const ins = await supabase.from('role_permissions').insert(rows);
  if (ins.error) throw new Error(ins.error.message);
}

export async function deleteRole(id: string): Promise<void> {
  const { error } = await supabase.from('roles').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Assign a user to a role. The token hook picks it up on their next refresh. */
export async function setUserRoleId(uid: string, roleId: string): Promise<void> {
  const { error } = await supabase.from('profiles').update({ role_id: roleId }).eq('id', uid);
  if (error) throw new Error(error.message);
}
